import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PatternBand } from './_brandPattern';

// ============================================================================
// Relevant-photo selection
// ============================================================================

/**
 * Keyword sets that map a page's `photoCategory` to the words we look for in
 * each gallery photo's title / description / alt text. The gallery has no
 * structured category field (only free text), so — like FloorplansList's
 * interior-photo picker — we score photos by keyword hits and pick the best
 * match, giving each interior page a hero photo relevant to its subject.
 */
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  interior: [
    'interior', 'apartment', 'unit', 'living', 'living room', 'kitchen', 'bedroom',
    'bath', 'bathroom', 'dining', 'home', 'closet', 'hardwood', 'granite', 'stainless',
    'appliance', 'window', 'floor', 'cabinet', 'counter',
  ],
  amenities: [
    'amenity', 'pool', 'fitness', 'gym', 'clubhouse', 'lounge', 'spa', 'sauna',
    'courtyard', 'grill', 'bbq', 'business', 'coffee', 'game', 'theater', 'yoga',
    'rooftop', 'deck', 'fire pit', 'cabana', 'club', 'social',
  ],
  pool: ['pool', 'swim', 'cabana', 'sundeck', 'sun deck', 'hot tub', 'spa', 'lounge'],
  fitness: ['fitness', 'gym', 'yoga', 'exercise', 'workout', 'weight', 'training'],
  community: [
    'exterior', 'building', 'community', 'aerial', 'entrance', 'lobby', 'leasing',
    'courtyard', 'landscap', 'grounds', 'facade', 'property',
  ],
  exterior: ['exterior', 'building', 'facade', 'aerial', 'entrance', 'street', 'architecture', 'front'],
  neighborhood: [
    'neighborhood', 'downtown', 'park', 'shopping', 'dining', 'district', 'skyline',
    'street', 'local', 'attraction', 'city', 'restaurant',
  ],
  pets: ['pet', 'dog', 'paw', 'bark park', 'dog park', 'pet spa', 'puppy', 'park'],
  lobby: ['lobby', 'entrance', 'reception', 'leasing', 'lounge', 'clubhouse', 'welcome'],
};

/** Strips query params / trailing slashes so CDN variants of one image
    (e.g. ?width=400 vs ?width=800) dedupe to a single photo. */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return keywords.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Picks the gallery index whose photo best fits `category`, using `rank` to
 * disambiguate pages that share a category (0 = best match, 1 = second best).
 * Falls back to `fallbackIndex` (wrapped into range) when there's no category,
 * no gallery, or no photo scores above zero — so every page still resolves to
 * a valid, distinct photo even when gallery metadata is sparse.
 */
function pickPhotoIndex(
  gallery: PhotoGallery[],
  category: string | undefined,
  rank: number,
  fallbackIndex: number,
): number {
  const wrappedFallback = gallery.length ? ((fallbackIndex % gallery.length) + gallery.length) % gallery.length : fallbackIndex;
  const keywords = category ? CATEGORY_KEYWORDS[category] : undefined;
  if (!gallery.length || !keywords) return wrappedFallback;

  const seen = new Set<string>();
  const scored: { index: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ index: i, score: scorePhoto(photo, keywords) });
  }

  const matches = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
  if (!matches.length) return wrappedFallback;
  return matches[Math.min(rank, matches.length - 1)].index;
}

// ============================================================================
// Slot group definition
// ============================================================================

/**
 * Builds the background-image slot group bound to a specific photo-gallery
 * index, so each interior page hero shows a DISTINCT property photo (rather
 * than every page reusing `property_featured_image`). The `sourceBinding`
 * keeps the image + alt text coherent when an editor picks a new gallery item.
 */
function makeBgImageDef(index: number): SlotGroupDefinition {
  return {
    label: 'Background Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
        defaultValue: 'Page banner',
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// Static definition (gallery index 0) for the schema-lint anchor. The visible
// wrapper uses the per-instance dynamic def from makeBgImageDef(imageIndex).
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Page banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

interface HeroImageBGProps {
  /** Per-instance path prefix (see shared-component pattern). Falls back to
      the canonical slot name so the component works standalone. */
  tokenPrefix?: string;
  /** Default H1 copy for this instance (e.g. "Floor Plans"). */
  title?: string;
  /** Fallback alt text for the background image. */
  altText?: string;
  /** Names a keyword set (see CATEGORY_KEYWORDS) used to auto-select the most
      relevant gallery photo for this page (e.g. "amenities", "neighborhood"). */
  photoCategory?: string;
  /** Disambiguates pages that share a `photoCategory` — 0 picks the best
      match, 1 the second best, etc. Defaults to 0. */
  photoRank?: number;
  /** Fallback gallery index (wrapped into range) when no category is set or no
      photo matches, so each page still shows a distinct image. Defaults to 0. */
  imageIndex?: number;
}

/**
 * Interior-page hero: a tall, full-bleed background image that nearly fills
 * the viewport with only the page H1 centered over it, capped by the same
 * decorative brand-pattern band that sits under the homepage hero. Shares the
 * `tokenPrefix` pattern with `HeroSolidBG` so it's a drop-in replacement in
 * `pages.json` — each page instance stores its own title + image override.
 */
export function HeroImageBG({
  tokenPrefix,
  title = 'Page',
  altText,
  photoCategory,
  photoRank = 0,
  imageIndex = 0,
}: HeroImageBGProps) {
  const prefix = tokenPrefix || 'heroimagebg';
  const titlePath = `${prefix}.title`;
  const bgPath = `${prefix}.bg_image`;

  // Resolve the most relevant gallery photo for this page, then build the
  // runtime slot def bound to that index (may differ from the static schema
  // anchor's index 0). Editors can still override the picked photo.
  const gallery = useGallery();
  const resolvedIndex = useMemo(
    () => pickPhotoIndex(gallery, photoCategory, photoRank, imageIndex),
    [gallery, photoCategory, photoRank, imageIndex],
  );
  const runtimeBgDef = useMemo(() => makeBgImageDef(resolvedIndex), [resolvedIndex]);
  const bg = useSlotGroup(bgPath, runtimeBgDef);

  return (
    <>
      <section className="hero-image-bg relative w-full overflow-hidden">
        {/* Full-bleed background image. The fixed header overlays the top of
            the image; the content below is padded down to clear it. */}
        <EditableSlotGroup groupPath={bgPath} definition={runtimeBgDef}>
          <div className="absolute inset-0">
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || altText || `${title} banner`}
                priority
                sizes="100vw"
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Gradient overlay for text contrast — decorative, never intercepts
            clicks so anything floating above the hero stays reachable. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/45 pointer-events-none" />

        {/* Centered H1. The container is absolutely filled and padded down by
            the header height (+ offer banner) so the title reads as centered
            within the VISIBLE region rather than behind the fixed header. */}
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-white px-6 pointer-events-none"
          style={{
            paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)',
            transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-image-bg__title font-heading text-2xl sm:text-3xl md:text-[2.5rem] lg:text-5xl 2xl:text-7xl uppercase tracking-wider leading-[1.15] w-full max-w-5xl text-balance break-words pointer-events-auto"
          />
        </div>
      </section>

      {/* Decorative brand-pattern band below the hero — same motif as the
          homepage, but shorter (70px vs. 100px) on interior pages. */}
      <PatternBand className="bg-[var(--vibe-color-surface)]" height={70} />

      {/* Schema-lint anchors — hidden, never interactive. They carry the
          canonical literal paths so the static linter can match every schema
          key; the visible wrappers above use the per-instance dynamic path. */}
      <RichTextContent
        path="heroimagebg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="heroimagebg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Mobile (< md): start from the homepage hero's mobile height
           (Hero.tsx uses h-[calc(70vh-50px)]) and trim a further 100px so
           interior heroes read shorter than the home hero on phones. */
        .hero-image-bg {
          min-height: calc(70vh - 150px);
        }
        /* Desktop (>= md): restore the original taller interior-hero height. */
        @media (min-width: 768px) {
          .hero-image-bg {
            min-height: calc(92vh - var(--offer-banner-height, 0px) - 100px);
          }
        }
        /* Theme heading font (DM Serif Display via --vibe-font-heading).
           Long titles wrap to 2+ balanced lines via max-w + text-balance;
           nested richtext <p> / <br> stay flush so multi-line edits look clean. */
        .hero-image-bg__title {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.35);
        }
        .hero-image-bg__title p {
          margin: 0;
        }
        .hero-image-bg__title p + p {
          margin-top: 0.15em;
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroImageBG',
  displayName: 'Hero – Image Background',
  tokens: {
    'heroimagebg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
  },
  slotGroups: {
    'heroimagebg.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'photo_gallery[0].alt_text',
          defaultValue: 'Page banner',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(HeroImageBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroImageBG;
